import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Search, X, Plus, Users, Hash, Lock, FolderKanban, Pin, CornerDownRight, ChevronDown, ChevronUp, Calendar, Phone, ArrowLeft } from 'lucide-react';
import { API_URL } from '../config';

const Messagerie = ({ employees = [], currentUser, authToken }) => {
  const [mode, setMode] = useState('direct'); // 'direct' ou 'channels'
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelMessages, setChannelMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [channelMembers, setChannelMembers] = useState([]);
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState([]);
  const [channelForm, setChannelForm] = useState({
    name: '',
    description: '',
    type: 'private',
    project_id: '',
    member_ids: []
  });
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadReplies, setThreadReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinned, setShowPinned] = useState(false);

  // Canaux d'événements
  const [eventCanaux, setEventCanaux] = useState([]);
  const [selectedEventCanal, setSelectedEventCanal] = useState(null);
  const [eventCanalMessages, setEventCanalMessages] = useState([]);

  // Charger tous les messages directs
  const loadMessages = async () => {
    if (!currentUser || !authToken) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/messages`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (!response.ok) {
        setMessages([]);
        setConversations([]);
        return;
      }
      const data = await response.json();
      setMessages(Array.isArray(data) ? data : []);

      // Grouper les messages par conversation
      const conversationsMap = new Map();

      data.forEach(msg => {
        const otherUserId = msg.sender_id === currentUser.employee_id
          ? msg.receiver_id
          : msg.sender_id;

        if (!conversationsMap.has(otherUserId)) {
          conversationsMap.set(otherUserId, {
            userId: otherUserId,
            messages: [],
            lastMessage: msg,
            unreadCount: 0
          });
        }

        conversationsMap.get(otherUserId).messages.push(msg);

        // Compter les non lus
        if (msg.receiver_id === currentUser.employee_id && msg.read_status === 0) {
          conversationsMap.get(otherUserId).unreadCount++;
        }
      });

      setConversations(Array.from(conversationsMap.values()));
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Charger les canaux
  const loadChannels = async () => {
    if (!authToken) return;

    try {
      const response = await fetch(`${API_URL}/channels`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await response.json();
      setChannels(data);
    } catch (error) {
      console.error('Erreur chargement canaux:', error);
    }
  };

  // Charger les canaux d'événements de l'utilisateur
  const loadEventCanaux = async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_URL}/canaux/mes-evenements`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.ok) setEventCanaux(await res.json());
    } catch (e) { console.error('Erreur canaux événements:', e); }
  };

  // Charger les messages d'un canal d'événement
  const loadEventCanalMessages = async (canalId) => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_URL}/canaux/${canalId}/messages`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.ok) setEventCanalMessages(await res.json());
    } catch (e) { setEventCanalMessages([]); }
  };

  // Sélectionner un canal d'événement
  const selectEventCanal = (canal) => {
    setSelectedEventCanal(canal);
    setSelectedChannel(null);
    setSelectedUser(null);
    setSelectedThread(null);
    setPinnedMessages([]);
    setShowPinned(false);
    loadEventCanalMessages(canal.id);
  };

  // Envoyer un message dans un canal d'événement
  const sendEventCanalMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedEventCanal) return;
    try {
      await fetch(`${API_URL}/canaux/${selectedEventCanal.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ message: newMessage })
      });
      setNewMessage('');
      loadEventCanalMessages(selectedEventCanal.id);
    } catch (e) { console.error('Erreur envoi message événement:', e); }
  };

  // Charger les projets
  const loadProjects = async () => {
    if (!authToken) return;

    try {
      const response = await fetch(`${API_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error('Erreur chargement projets:', error);
    }
  };

  // Charger les messages d'un canal
  const loadChannelMessages = async (channelId) => {
    if (!authToken) return;

    try {
      const response = await fetch(`${API_URL}/channels/${channelId}/messages`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await response.json();
      setChannelMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement messages canal:', error);
      setChannelMessages([]);
    }
  };

  useEffect(() => {
    if (mode === 'direct') {
      loadMessages();
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    } else {
      loadChannels();
      loadProjects();
      loadEventCanaux();
    }
  }, [currentUser, authToken, mode]);

  // Polling messages canal d'événement sélectionné
  useEffect(() => {
    if (!selectedEventCanal) return;
    const interval = setInterval(() => loadEventCanalMessages(selectedEventCanal.id), 5000);
    return () => clearInterval(interval);
  }, [selectedEventCanal, authToken]);

  useEffect(() => {
    if (selectedChannel) {
      loadChannelMessages(selectedChannel.id);
      const interval = setInterval(() => loadChannelMessages(selectedChannel.id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedChannel, authToken]);

  // Envoyer un message direct
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser || !currentUser) return;

    setLoading(true);
    try {
      await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          sender_id: currentUser.employee_id,
          receiver_id: selectedUser.id,
          message: newMessage
        })
      });

      setNewMessage('');
      await loadMessages();
    } catch (error) {
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  // Envoyer un message dans un canal
  const handleSendChannelMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChannel) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/channels/${selectedChannel.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          message: newMessage
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de l\'envoi');
      }

      setNewMessage('');
      await loadChannelMessages(selectedChannel.id);
    } catch (error) {
      console.error('Erreur envoi message canal:', error);
      alert('Erreur lors de l\'envoi du message: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si l'utilisateur peut gérer les membres (Manager, Super Manager ou Patron)
  const canManageMembers = () => {
    return currentUser && ['Patron', 'Super Manager', 'Manager'].includes(currentUser.role_name);
  };

  // Charger les membres d'un canal
  const loadChannelMembers = async (channelId) => {
    try {
      const response = await fetch(`${API_URL}/channels/${channelId}/members`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await response.json();
      setChannelMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement membres:', error);
      setChannelMembers([]);
    }
  };

  // Quitter un canal
  const handleLeaveChannel = async () => {
    if (!selectedChannel || !currentUser) return;

    if (!window.confirm(`Voulez-vous quitter le canal "${selectedChannel.name}" ?`)) return;

    try {
      const response = await fetch(
        `${API_URL}/channels/${selectedChannel.id}/members/${currentUser.employee_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur');
      }

      alert('Vous avez quitté le canal');
      setSelectedChannel(null);
      setShowManageMembers(false);
      await loadChannels();
    } catch (error) {
      console.error('Erreur quitter canal:', error);
      alert('Erreur: ' + error.message);
    }
  };

  // Supprimer un canal (Manager/Patron uniquement)
  const handleDeleteChannel = async () => {
    if (!selectedChannel || !canManageMembers()) return;

    if (!window.confirm(`Voulez-vous supprimer le canal "${selectedChannel.name}" ? Tous les messages seront perdus.`)) return;

    try {
      const response = await fetch(
        `${API_URL}/channels/${selectedChannel.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur');
      }

      alert('Canal supprimé avec succès');
      setSelectedChannel(null);
      setShowManageMembers(false);
      await loadChannels();
    } catch (error) {
      console.error('Erreur suppression canal:', error);
      alert('Erreur: ' + error.message);
    }
  };

  // Ajouter des membres au canal (Manager/Patron uniquement)
  const handleAddMembers = async () => {
    if (!selectedChannel || !canManageMembers() || selectedMembersToAdd.length === 0) return;

    try {
      const response = await fetch(
        `${API_URL}/channels/${selectedChannel.id}/members`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            employee_ids: selectedMembersToAdd
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur');
      }

      alert('Membres ajoutés avec succès');
      setSelectedMembersToAdd([]);
      await loadChannelMembers(selectedChannel.id);
    } catch (error) {
      console.error('Erreur ajout membres:', error);
      alert('Erreur: ' + error.message);
    }
  };

  // Retirer un membre du canal (Manager/Patron uniquement)
  const handleRemoveMember = async (memberId) => {
    if (!selectedChannel || !canManageMembers()) return;

    if (!window.confirm('Voulez-vous retirer ce membre du canal ?')) return;

    try {
      const response = await fetch(
        `${API_URL}/channels/${selectedChannel.id}/members/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur');
      }

      alert('Membre retiré avec succès');
      await loadChannelMembers(selectedChannel.id);
    } catch (error) {
      console.error('Erreur retrait membre:', error);
      alert('Erreur: ' + error.message);
    }
  };

  // Créer un nouveau canal
  const handleCreateChannel = async (e) => {
    e.preventDefault();

    if (!channelForm.name) {
      alert('Le nom du canal est requis');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(channelForm)
      });

      if (!response.ok) throw new Error('Erreur création canal');

      await loadChannels();
      setShowCreateChannel(false);
      setChannelForm({
        name: '',
        description: '',
        type: 'private',
        project_id: '',
        member_ids: []
      });
      alert('Canal créé avec succès !');
    } catch (error) {
      alert('Erreur lors de la création du canal');
    }
  };

  // Marquer comme lu
  const markAsRead = async (messageId) => {
    try {
      await fetch(`${API_URL}/messages/${messageId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      await loadMessages();
    } catch (error) {
      console.error('Erreur marquer comme lu:', error);
    }
  };

  // Sélectionner une conversation
  const selectConversation = (userId) => {
    const user = employees.find(e => e.id === userId);
    setSelectedUser(user);
    setSelectedChannel(null);
    setSelectedEventCanal(null);
    setMobileShowChat(true);

    const conversation = conversations.find(c => c.userId === userId);
    if (conversation) {
      conversation.messages.forEach(msg => {
        if (msg.receiver_id === currentUser.employee_id && msg.read_status === 0) {
          markAsRead(msg.id);
        }
      });
    }
  };

  // Sélectionner un canal
  const selectChannel = (channel) => {
    setSelectedChannel(channel);
    setSelectedUser(null);
    setSelectedEventCanal(null);
    setSelectedThread(null);
    setPinnedMessages([]);
    setShowPinned(false);
    loadPinnedMessages(channel.id);
    setMobileShowChat(true);
  };

  // Nouvelle conversation
  const startNewConversation = (user) => {
    setSelectedUser(user);
    setSelectedChannel(null);
    setMobileShowChat(true);
  };

  // Filtrer les employés pour nouvelle conversation
  const availableUsers = employees.filter(emp =>
    emp.id !== currentUser?.employee_id &&
    (emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Basculer la sélection d'un membre dans le formulaire de canal
  const toggleMember = (employeeId) => {
    setChannelForm(prev => ({
      ...prev,
      member_ids: prev.member_ids.includes(employeeId)
        ? prev.member_ids.filter(id => id !== employeeId)
        : [...prev.member_ids, employeeId]
    }));
  };

  // Charger les messages épinglés du canal
  const loadPinnedMessages = async (channelId) => {
    try {
      const res = await fetch(`${API_URL}/channels/${channelId}/messages-epingles`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setPinnedMessages(Array.isArray(data) ? data : []);
    } catch (e) { setPinnedMessages([]); }
  };

  // Épingler / désépingler un message
  const togglePin = async (msg) => {
    const isAlreadyPinned = pinnedMessages.some(p => p.message_id === msg.id);
    const url = `${API_URL}/channels/${selectedChannel.id}/epingler/${msg.id}`;
    await fetch(url, {
      method: isAlreadyPinned ? 'DELETE' : 'POST',
      headers: { Authorization: `Bearer ${authToken}` }
    });
    loadPinnedMessages(selectedChannel.id);
  };

  // Charger les réponses d'un message (thread)
  const loadReplies = async (msgId) => {
    try {
      const res = await fetch(`${API_URL}/messages/${msgId}/replies`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setThreadReplies(Array.isArray(data) ? data : []);
    } catch (e) { setThreadReplies([]); }
  };

  const openThread = (msg) => {
    setSelectedThread(msg);
    loadReplies(msg.id);
    setNewReply('');
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim() || !selectedThread) return;
    await fetch(`${API_URL}/messages/${selectedThread.id}/replies`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ contenu: newReply.trim() })
    });
    setNewReply('');
    loadReplies(selectedThread.id);
  };

  // Messages de la conversation sélectionnée
  const currentConversation = selectedUser
    ? conversations.find(c => c.userId === selectedUser.id)?.messages || []
    : [];

  const sortedMessages = currentConversation.sort((a, b) =>
    new Date(a.created_at) - new Date(b.created_at)
  );

  // État mobile : afficher la liste ou la conversation
  const [mobileShowChat, setMobileShowChat] = useState(false);

  // Sélectionner un utilisateur (+ basculer en mode chat sur mobile)
  const selectUserMobile = (user) => {
    setSelectedUser(user);
    setSelectedChannel(null);
    setMobileShowChat(true);
  };

  // Sélectionner un canal (+ basculer en mode chat sur mobile)
  const selectChannelMobile = (channel) => {
    selectChannel(channel);
    setMobileShowChat(true);
  };

  // Retour à la liste sur mobile
  const backToList = () => {
    setMobileShowChat(false);
  };

  return (
    <div className="h-[calc(100vh-180px)] sm:h-[calc(100vh-200px)]">
      <div className="bg-surface rounded-xl shadow-lg overflow-hidden h-full flex">
        {/* Liste des conversations / canaux */}
        <div className={`w-full md:w-1/3 border-r border-border-subtle flex flex-col ${mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
          {/* En-tête */}
          <div className="p-4 border-b border-border-subtle bg-gradient-to-r from-blue-500 to-blue-600">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-3">
              <MessageSquare className="w-6 h-6" />
              Messagerie
            </h2>

            {/* Onglets */}
            <div className="flex gap-2">
              <button
                onClick={() => setMode('direct')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                  mode === 'direct'
                    ? 'bg-surface text-blue-600 dark:text-blue-400'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Messages directs
              </button>
              <button
                onClick={() => setMode('channels')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                  mode === 'channels'
                    ? 'bg-surface text-blue-600 dark:text-blue-400'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Canaux
              </button>
            </div>
          </div>

          {mode === 'direct' ? (
            <>
              {/* Nouvelle conversation */}
              <div className="p-4 border-b border-border-subtle">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="text"
                    placeholder="Rechercher un employé..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {searchTerm && (
                  <div className="mt-2 max-h-40 overflow-y-auto border border-border-subtle rounded-lg">
                    {availableUsers.map(user => (
                      <button
                        key={user.id}
                        onClick={() => {
                          startNewConversation(user);
                          setSearchTerm('');
                        }}
                        className="w-full p-2 hover:bg-elevated-1 flex items-center space-x-2 text-left"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                          {user.avatar}
                        </div>
                        <span className="text-sm">{user.first_name} {user.last_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Liste des conversations */}
              <div className="flex-1 overflow-y-auto">
                {conversations.length > 0 ? (
                  conversations.map(conv => {
                    const user = employees.find(e => e.id === conv.userId);
                    if (!user) return null;

                    return (
                      <button
                        key={conv.userId}
                        onClick={() => selectConversation(conv.userId)}
                        className={`w-full p-4 border-b border-border-subtle hover:bg-elevated-1 text-left transition ${
                          selectedUser?.id === conv.userId ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                            {user.avatar}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-semibold text-text-primary truncate">
                                {user.first_name} {user.last_name}
                              </p>
                              {conv.unreadCount > 0 && (
                                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                  {conv.unreadCount}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-text-secondary truncate">
                              {conv.lastMessage.message}
                            </p>
                            <p className="text-xs text-text-muted mt-1">
                              {new Date(conv.lastMessage.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="p-8 text-center text-text-muted">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Aucune conversation</p>
                    <p className="text-sm mt-1">Recherchez un employé pour commencer</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Bouton créer canal */}
              <div className="p-4 border-b border-border-subtle">
                <button
                  onClick={() => setShowCreateChannel(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition"
                >
                  <Plus className="w-5 h-5" />
                  <span>Créer un canal</span>
                </button>
              </div>

              {/* Liste des canaux */}
              <div className="flex-1 overflow-y-auto">
                {/* Canaux classiques */}
                {channels.length > 0 && (
                  <div>
                    <p className="px-4 pt-3 pb-1 text-xs font-semibold text-text-muted uppercase tracking-wider">Canaux</p>
                    {channels.map(channel => (
                      <button
                        key={channel.id}
                        onClick={() => selectChannel(channel)}
                        className={`w-full p-4 border-b border-border-subtle hover:bg-elevated-1 text-left transition ${
                          selectedChannel?.id === channel.id ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white flex-shrink-0">
                            {channel.type === 'project' ? (
                              <FolderKanban className="w-5 h-5" />
                            ) : channel.type === 'private' ? (
                              <Lock className="w-5 h-5" />
                            ) : (
                              <Hash className="w-5 h-5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-text-primary truncate">{channel.name}</p>
                            <p className="text-sm text-text-muted truncate">
                              {channel.description || `${channel.member_count} membre(s)`}
                            </p>
                            {channel.project_name && (
                              <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">📁 {channel.project_name}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Canaux d'événements */}
                {eventCanaux.length > 0 && (
                  <div>
                    <p className="px-4 pt-3 pb-1 text-xs font-semibold text-text-muted uppercase tracking-wider border-t border-border-subtle">
                      📅 Événements
                    </p>
                    {eventCanaux.map(canal => (
                      <button
                        key={canal.id}
                        onClick={() => selectEventCanal(canal)}
                        className={`w-full p-4 border-b border-border-subtle hover:bg-elevated-1 text-left transition ${
                          selectedEventCanal?.id === canal.id ? 'bg-orange-50 dark:bg-orange-950/40' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white flex-shrink-0">
                            <Calendar className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-text-primary truncate">{canal.evenement_nom || canal.nom}</p>
                            {canal.lieu && (
                              <p className="text-xs text-text-muted truncate">📍 {canal.lieu}</p>
                            )}
                            {canal.last_message ? (
                              <p className="text-xs text-text-muted truncate mt-0.5">{canal.last_message}</p>
                            ) : (
                              <p className="text-xs text-text-muted italic mt-0.5">{canal.membre_count} membre(s)</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {channels.length === 0 && eventCanaux.length === 0 && (
                  <div className="p-8 text-center text-text-muted">
                    <Hash className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Aucun canal</p>
                    <p className="text-sm mt-1">Créez un canal pour commencer</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Zone de conversation / canal */}
        <div className={`w-full md:w-2/3 flex flex-col ${mobileShowChat ? 'flex' : 'hidden md:flex'}`}>
          {selectedUser ? (
            <>
              {/* En-tête conversation */}
              <div className="p-3 sm:p-4 border-b border-border-subtle bg-elevated-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    <button
                      onClick={backToList}
                      className="md:hidden p-1 rounded-lg hover:bg-elevated-1 text-text-secondary transition"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                      {selectedUser.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary text-sm md:text-base">
                        {selectedUser.first_name} {selectedUser.last_name}
                      </p>
                      <p className="text-sm text-text-secondary">{selectedUser.role_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 sm:space-x-2 flex-shrink-0">
                    <a
                      href={selectedUser.phone
                        ? `https://wa.me/${selectedUser.phone.replace(/[^0-9]/g, '').replace(/^0/, '33')}?text=${encodeURIComponent(`Salut ${selectedUser.first_name}, `)}`
                        : `https://wa.me/?text=${encodeURIComponent(`Salut ${selectedUser.first_name}, `)}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold transition"
                      title="Envoyer sur WhatsApp"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      <span className="hidden sm:inline">WhatsApp</span>
                    </a>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="text-text-muted hover:text-text-secondary"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 bg-elevated-1 space-y-4">
                {sortedMessages.map(msg => {
                  const isSentByMe = msg.sender_id === currentUser.employee_id;

                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          isSentByMe
                            ? 'bg-blue-600 text-white'
                            : 'bg-surface text-text-primary border border-border-subtle'
                        }`}
                      >
                        <p className="break-words">{msg.message}</p>
                        <p
                          className={`text-xs mt-1 ${
                            isSentByMe ? 'text-blue-100' : 'text-text-muted'
                          }`}
                        >
                          {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Formulaire d'envoi */}
              <form onSubmit={handleSendMessage} className="p-3 sm:p-4 border-t border-border-subtle bg-surface">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Tapez votre message..."
                    className="flex-1 min-w-0 px-3 sm:px-4 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                  />
                  <button
                    type="submit"
                    disabled={loading || !newMessage.trim()}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-6 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition disabled:opacity-50 flex-shrink-0"
                  >
                    <Send className="w-5 h-5" />
                    <span className="hidden sm:inline">Envoyer</span>
                  </button>
                  {newMessage.trim() && (
                    <a
                      href={selectedUser?.phone
                        ? `https://wa.me/${selectedUser.phone.replace(/[^0-9]/g, '').replace(/^0/, '33')}?text=${encodeURIComponent(newMessage)}`
                        : `https://wa.me/?text=${encodeURIComponent(newMessage)}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-1 transition"
                      title="Envoyer aussi sur WhatsApp"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </a>
                  )}
                </div>
              </form>
            </>
          ) : selectedChannel ? (
            <>
              {/* En-tête canal */}
              <div className="p-3 sm:p-4 border-b border-border-subtle bg-purple-50 dark:bg-purple-950/40">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    <button
                      onClick={backToList}
                      className="md:hidden p-1 rounded-lg hover:bg-purple-100 dark:bg-purple-900/40 text-text-secondary transition flex-shrink-0"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white flex-shrink-0">
                      {selectedChannel.type === 'project' ? (
                        <FolderKanban className="w-4 h-4 sm:w-6 sm:h-6" />
                      ) : (
                        <Hash className="w-4 h-4 sm:w-6 sm:h-6" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-text-primary text-sm sm:text-base truncate">
                        {selectedChannel.name}
                      </p>
                      <p className="text-xs sm:text-sm text-text-secondary truncate">
                        {selectedChannel.description || `${selectedChannel.member_count} membre(s)`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setShowPinned(!showPinned)}
                      className={`p-1.5 sm:px-3 sm:py-1 rounded transition text-sm font-medium ${showPinned ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' : 'text-purple-600 hover:bg-purple-100 dark:bg-purple-900/40'}`}
                      title="Épinglés"
                    >
                      <Pin className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">{pinnedMessages.length > 0 ? pinnedMessages.length : ''} Épinglés</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowManageMembers(true);
                        loadChannelMembers(selectedChannel.id);
                      }}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-100 dark:bg-purple-900/40 p-1.5 sm:px-3 sm:py-1 rounded transition text-sm font-medium flex items-center"
                      title="Membres"
                    >
                      <Users className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Membres</span>
                    </button>
                    {canManageMembers() && (
                      <button
                        onClick={handleDeleteChannel}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-950/40 p-1.5 sm:px-3 sm:py-1 rounded transition text-sm font-medium hidden sm:block"
                      >
                        Supprimer
                      </button>
                    )}
                    <button
                      onClick={handleLeaveChannel}
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:bg-orange-950/40 p-1.5 sm:px-3 sm:py-1 rounded transition text-sm font-medium hidden sm:block"
                    >
                      Quitter
                    </button>
                    <button
                      onClick={() => setSelectedChannel(null)}
                      className="text-text-muted hover:text-text-secondary"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages épinglés (panel repliable) */}
              {showPinned && (
                <div className="border-b border-border-subtle bg-yellow-50 dark:bg-yellow-950/40 px-4 py-3 max-h-48 overflow-y-auto">
                  <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-300 mb-2 flex items-center gap-1"><Pin size={12} /> Messages épinglés ({pinnedMessages.length})</p>
                  {pinnedMessages.length === 0 ? (
                    <p className="text-xs text-text-muted italic">Aucun message épinglé</p>
                  ) : (
                    pinnedMessages.map(p => (
                      <div key={p.message_id} className="bg-surface border border-yellow-200 dark:border-yellow-800 rounded px-3 py-2 mb-1 text-sm text-text-secondary">
                        <span className="font-medium text-text-muted text-xs">{p.sender_nom} ·</span> {p.content}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Messages du canal */}
              <div className="flex-1 overflow-y-auto p-4 bg-elevated-1 space-y-4">
                {Array.isArray(channelMessages) && channelMessages.length > 0 ? (
                  channelMessages.map(msg => {
                    const isSentByMe = msg.sender_id === currentUser.employee_id;
                    const isPinned = pinnedMessages.some(p => p.message_id === msg.id);

                    return (
                      <div key={msg.id} className="group flex items-start space-x-3">
                        {!isSentByMe && (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {msg.sender_avatar}
                          </div>
                        )}
                        <div className={`flex-1 ${isSentByMe ? 'text-right' : ''}`}>
                          {!isSentByMe && (
                            <p className="text-sm font-semibold text-text-secondary mb-1">
                              {msg.sender_first_name} {msg.sender_last_name}
                            </p>
                          )}
                          <div
                            className={`inline-block px-4 py-2 rounded-lg ${
                              isSentByMe
                                ? 'bg-purple-600 text-white'
                                : 'bg-surface text-text-primary border border-border-subtle'
                            } ${isPinned ? 'ring-2 ring-yellow-400' : ''}`}
                          >
                            <p className="break-words">{msg.message}</p>
                            <p
                              className={`text-xs mt-1 ${
                                isSentByMe ? 'text-purple-100' : 'text-text-muted'
                              }`}
                            >
                              {new Date(msg.created_at).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          {/* Actions message */}
                          <div className={`flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${isSentByMe ? 'justify-end' : 'justify-start'}`}>
                            <button
                              onClick={() => openThread(msg)}
                              className="flex items-center gap-1 text-xs text-text-muted hover:text-purple-600 px-2 py-1 rounded hover:bg-purple-50 dark:bg-purple-950/40"
                              title="Répondre dans un fil"
                            >
                              <CornerDownRight size={12} /> Fil
                            </button>
                            <button
                              onClick={() => togglePin(msg)}
                              className={`flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-yellow-50 dark:bg-yellow-950/40 ${isPinned ? 'text-yellow-600 dark:text-yellow-400' : 'text-text-muted hover:text-yellow-600 dark:text-yellow-400'}`}
                              title={isPinned ? 'Désépingler' : 'Épingler'}
                            >
                              <Pin size={12} /> {isPinned ? 'Désépingler' : 'Épingler'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center h-full text-text-muted">
                    <p>Aucun message dans ce canal. Soyez le premier à écrire !</p>
                  </div>
                )}
              </div>

              {/* Panel thread */}
              {selectedThread && (
                <div className="border-t border-border-subtle bg-surface flex flex-col" style={{ maxHeight: '40%' }}>
                  <div className="flex items-center justify-between px-4 py-2 border-b border-border-subtle bg-elevated-1">
                    <p className="text-sm font-semibold text-text-secondary flex items-center gap-1">
                      <CornerDownRight size={14} /> Fil de discussion
                    </p>
                    <button onClick={() => setSelectedThread(null)} className="text-text-muted hover:text-text-secondary"><X size={16} /></button>
                  </div>
                  <div className="px-4 py-2 border-b border-border-subtle bg-yellow-50 dark:bg-yellow-950/40">
                    <p className="text-xs text-text-muted">{selectedThread.sender_first_name} {selectedThread.sender_last_name}</p>
                    <p className="text-sm text-text-secondary">{selectedThread.message}</p>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
                    {threadReplies.length === 0 ? (
                      <p className="text-xs text-text-muted italic">Aucune réponse pour l'instant</p>
                    ) : (
                      threadReplies.map(r => (
                        <div key={r.id} className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-xs flex-shrink-0">
                            {r.sender_nom?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-text-secondary">{r.sender_nom}</p>
                            <p className="text-sm text-text-primary">{r.contenu}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <form onSubmit={sendReply} className="flex gap-2 p-3 border-t border-border-subtle">
                    <input
                      className="flex-1 border border-border-strong rounded-lg px-3 py-1.5 text-sm"
                      placeholder="Répondre..."
                      value={newReply}
                      onChange={e => setNewReply(e.target.value)}
                    />
                    <button type="submit" disabled={!newReply.trim()}
                      className="bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                      <Send size={14} />
                    </button>
                  </form>
                </div>
              )}

              {/* Formulaire d'envoi canal */}
              <form onSubmit={handleSendChannelMessage} className="p-3 sm:p-4 border-t border-border-subtle bg-surface">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Envoyer un message au canal..."
                    className="flex-1 min-w-0 px-3 sm:px-4 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                  />
                  <button
                    type="submit"
                    disabled={loading || !newMessage.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-6 py-2 rounded-lg flex items-center space-x-1 sm:space-x-2 transition disabled:opacity-50 flex-shrink-0"
                  >
                    <Send className="w-5 h-5" />
                    <span className="hidden sm:inline">Envoyer</span>
                  </button>
                </div>
              </form>
            </>
          ) : selectedEventCanal ? (
            <>
              {/* En-tête canal événement */}
              <div className="p-3 sm:p-4 border-b border-border-subtle bg-orange-50 dark:bg-orange-950/40">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                    <button
                      onClick={backToList}
                      className="md:hidden p-1 rounded-lg hover:bg-orange-100 dark:bg-orange-900/40 text-text-secondary transition flex-shrink-0"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white flex-shrink-0">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-text-primary text-sm sm:text-base truncate">
                        {selectedEventCanal.evenement_nom || selectedEventCanal.nom}
                      </p>
                      <p className="text-xs sm:text-sm text-text-muted truncate">
                        {selectedEventCanal.lieu && `📍 ${selectedEventCanal.lieu}`}
                        {selectedEventCanal.lieu && selectedEventCanal.date_debut && ' · '}
                        {selectedEventCanal.date_debut && new Date(selectedEventCanal.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {` · ${selectedEventCanal.membre_count} membre(s)`}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => { setSelectedEventCanal(null); backToList(); }} className="text-text-muted hover:text-text-secondary flex-shrink-0">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Messages du canal événement */}
              <div className="flex-1 overflow-y-auto p-4 bg-elevated-1 space-y-3">
                {eventCanalMessages.length > 0 ? eventCanalMessages.map(msg => {
                  const isMine = msg.sender_id === currentUser?.employee_id;
                  return (
                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className="max-w-xs lg:max-w-md">
                        {!isMine && (
                          <p className="text-xs font-semibold text-text-secondary mb-1 px-1">{msg.sender_name}</p>
                        )}
                        <div className={`px-4 py-2 rounded-2xl ${
                          isMine
                            ? 'bg-orange-500 text-white rounded-br-sm'
                            : 'bg-surface text-text-primary border border-border-subtle rounded-bl-sm'
                        }`}>
                          <p className="text-sm break-words whitespace-pre-wrap">{msg.message}</p>
                          <p className={`text-xs mt-1 ${isMine ? 'text-orange-100' : 'text-text-muted'}`}>
                            {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="flex items-center justify-center h-full text-text-muted">
                    <p className="text-sm">Aucun message. Soyez le premier à écrire !</p>
                  </div>
                )}
              </div>

              {/* Formulaire envoi canal événement */}
              <form onSubmit={sendEventCanalMessage} className="p-3 sm:p-4 border-t border-border-subtle bg-surface">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Répondre dans ce canal d'événement..."
                    className="flex-1 min-w-0 px-3 sm:px-4 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-3 sm:px-6 py-2 rounded-lg flex items-center space-x-2 transition disabled:opacity-50 flex-shrink-0"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-elevated-1">
              <div className="text-center text-text-muted">
                <MessageSquare className="w-16 h-16 mx-auto mb-4" />
                <p className="text-lg">Sélectionnez une conversation ou un canal</p>
                <p className="text-sm mt-2">ou créez-en un nouveau</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal création de canal */}
      {showCreateChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Créer un canal</h2>
            <form onSubmit={handleCreateChannel} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nom du canal *</label>
                <input
                  type="text"
                  required
                  value={channelForm.name}
                  onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="support-client"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={channelForm.description}
                  onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="2"
                  placeholder="Description du canal..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Type *</label>
                <select
                  value={channelForm.type}
                  onChange={(e) => setChannelForm({ ...channelForm, type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  <option value="private">Privé</option>
                  <option value="project">Lié à un projet</option>
                </select>
              </div>

              {channelForm.type === 'project' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Projet</label>
                  <select
                    value={channelForm.project_id}
                    onChange={(e) => setChannelForm({ ...channelForm, project_id: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="">Sélectionner un projet</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  Membres ({channelForm.member_ids.length} sélectionné(s))
                </label>
                <div className="border rounded-lg p-3 max-h-60 overflow-y-auto space-y-2">
                  {employees
                    .filter(emp => emp.id !== currentUser?.employee_id)
                    .map(emp => (
                      <label
                        key={emp.id}
                        className="flex items-center space-x-3 p-2 hover:bg-elevated-1 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={channelForm.member_ids.includes(emp.id)}
                          onChange={() => toggleMember(emp.id)}
                          className="w-4 h-4"
                        />
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                          {emp.avatar}
                        </div>
                        <span className="text-sm">
                          {emp.first_name} {emp.last_name}
                        </span>
                      </label>
                    ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg"
                >
                  Créer le canal
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateChannel(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 py-2 rounded-lg"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal gestion des membres */}
      {showManageMembers && selectedChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Membres du canal</h2>
              <button
                onClick={() => setShowManageMembers(false)}
                className="text-text-muted hover:text-text-secondary"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Liste des membres actuels */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">
                Membres actuels ({channelMembers.length})
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                {channelMembers.length > 0 ? (
                  channelMembers.map(member => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-2 hover:bg-elevated-1 rounded"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold">
                          {member.avatar}
                        </div>
                        <div>
                          <p className="font-semibold text-text-primary">
                            {member.first_name} {member.last_name}
                            {member.id === currentUser?.employee_id && (
                              <span className="text-xs text-purple-600 dark:text-purple-400 ml-2">(Vous)</span>
                            )}
                          </p>
                          <p className="text-sm text-text-secondary">{member.role_name}</p>
                        </div>
                      </div>
                      {canManageMembers() && member.id !== currentUser?.employee_id && (
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:bg-red-950/40 px-3 py-1 rounded text-sm font-medium"
                        >
                          Retirer
                        </button>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-text-muted text-center py-4">Chargement...</p>
                )}
              </div>
            </div>

            {/* Ajouter des membres (Manager/Patron uniquement) */}
            {canManageMembers() && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Ajouter des membres</h3>
                <div className="space-y-4">
                  <div className="border rounded-lg p-3 max-h-60 overflow-y-auto space-y-2">
                    {employees
                      .filter(emp => !channelMembers.find(m => m.id === emp.id))
                      .map(emp => (
                        <label
                          key={emp.id}
                          className="flex items-center space-x-3 p-2 hover:bg-elevated-1 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedMembersToAdd.includes(emp.id)}
                            onChange={() => {
                              setSelectedMembersToAdd(prev =>
                                prev.includes(emp.id)
                                  ? prev.filter(id => id !== emp.id)
                                  : [...prev, emp.id]
                              );
                            }}
                            className="w-4 h-4"
                          />
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                            {emp.avatar}
                          </div>
                          <span className="text-sm">
                            {emp.first_name} {emp.last_name}
                          </span>
                        </label>
                      ))}
                  </div>

                  <button
                    onClick={handleAddMembers}
                    disabled={selectedMembersToAdd.length === 0}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Ajouter {selectedMembersToAdd.length > 0 && `(${selectedMembersToAdd.length})`}
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6 pt-4 border-t">
              <button
                onClick={() => setShowManageMembers(false)}
                className="w-full bg-gray-200 hover:bg-gray-300 py-2 rounded-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messagerie;
